import { Method, Method_Type as MethodType } from "gen/company/tender/models_pb"
import { useMemo } from "react"
import { useIdFilter } from "shared-ts/hooks/useFilter"

import { useSearchTenderMethods } from "features/api/search"
import { useWebFilter, UseWebFilterProps } from "hooks/filters/useWebFilter"

interface UseTenderMethodFilterProps
  extends Pick<
    UseWebFilterProps<unknown>,
    "persistInUrl" | "key" | "initialSelectedOptions"
  > {
  excludeWriteOff: boolean
  excludeChargeAccount: boolean
  customOnly: boolean
}

export function useTenderMethodFilter({
  key = "tenderMethod",
  persistInUrl = true,
  initialSelectedOptions = [],
  excludeWriteOff = false,
  excludeChargeAccount = false,
  customOnly = false,
}: Partial<UseTenderMethodFilterProps> = {}) {
  const excludeTypes = [
    ...(excludeWriteOff ? [MethodType.WRITE_OFF] : []),
    ...(excludeChargeAccount ? [MethodType.CHARGE_ACCOUNT] : []),
  ]
  const { results, setQuery, loadNext } = useSearchTenderMethods({
    excludeTypes,
    includeTypes: customOnly ? [MethodType.CUSTOM] : [],
  })

  const methods = useMemo(() => {
    const methods: Method[] = []
    for (const { method } of results) {
      if (!method) continue
      methods.push(method)
    }
    return methods
  }, [results])

  const { filter, selected: selectedMethodIds } = useIdFilter({
    key,
    label: "Tender method",
    values: methods,
    labelExtractor: (method) => method.name,
    onChangeQuery: setQuery,
    autoFilterOptions: false,
    allowExternalValues: true,
    onLoadMore: loadNext,
    useFilter: (props) => {
      return useWebFilter({
        ...props,
        persistInUrl,
        initialSelectedOptions,
      })
    },
  })

  return { filter, selectedMethodIds }
}
